import { Routes, Route } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { Suspense, lazy } from 'react'
import { NotFound } from './routes/NotFound'
import UnauthenticatedLayout from './components/public/layout/UnauthenticatedLayout'
import { ThemeProvider } from './components/theme-provider'

// Lazy load public components
const Home = lazy(() => import('./components/public/sections/Home'))
const LandingPage = lazy(() => import('./components/public/sections/LandingPage'))
const Lander1 = lazy(() => import('./components/public/pages/Lander1'))
const AboutUs = lazy(() => import('./components/public/pages/AboutUs'))
const Contact = lazy(() => import('./components/public/pages/Contact'))
// const PricingPage = lazy(() => import('./components/public/pricing/PricingPage')) // Pricing removed
const Products = lazy(() => import('./components/public/pages/Products'))
const Services = lazy(() => import('./components/public/pages/Services'))
const Auth = lazy(() => import('./components/public/auth/Auth'))
const AuthCallback = lazy(() => import('./components/public/auth/AuthCallback'))

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center p-8">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
)

// Generated with Fullstack Core Template v1.2.32
// Project: RoboRelay - The Operating System for Work Robots

function App({ mockAuth }: { mockAuth?: any }) {
  // Use mock auth in development if provided, otherwise use real Clerk auth
  const clerkAuth = useAuth()
  const auth = mockAuth || clerkAuth
  const { isSignedIn, isLoaded } = auth

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <ThemeProvider defaultTheme="light" storageKey="roborelay-ui-theme">
      <div className="min-h-screen bg-background text-foreground">
        {isSignedIn ? (
          // Authenticated user routes - TODO: Add authenticated layout
          <Routes>
            <Route path="/" element={
              <Suspense fallback={<LoadingFallback />}>
                <Home />
              </Suspense>
            } />
            
            {/* Public pages remain accessible for authenticated users */}
            <Route path="/lander" element={
              <Suspense fallback={<LoadingFallback />}>
                <LandingPage />
              </Suspense>
            } />
            <Route path="/lander1" element={
              <Suspense fallback={<LoadingFallback />}>
                <Lander1 />
              </Suspense>
            } />
            <Route path="/about" element={
              <Suspense fallback={<LoadingFallback />}>
                <AboutUs />
              </Suspense>
            } />
            <Route path="/contact" element={
              <Suspense fallback={<LoadingFallback />}>
                <Contact />
              </Suspense>
            } />
            {/* Pricing route removed
            <Route path="/pricing" element={
              <Suspense fallback={<LoadingFallback />}>
                <PricingPage />
              </Suspense>
            } /> */}
            <Route path="/products" element={
              <Suspense fallback={<LoadingFallback />}>
                <Products />
              </Suspense>
            } />
            <Route path="/services" element={
              <Suspense fallback={<LoadingFallback />}>
                <Services />
              </Suspense>
            } />
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        ) : (
          // Unauthenticated user routes
          <Routes>
            <Route path="/auth/callback" element={
              <Suspense fallback={<LoadingFallback />}>
                <AuthCallback />
              </Suspense>
            } />
            
            <Route element={<UnauthenticatedLayout />}>
              <Route path="/" element={
                <Suspense fallback={<LoadingFallback />}>
                  <Home />
                </Suspense>
              } />
              <Route path="/lander" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LandingPage />
                </Suspense>
              } />
              <Route path="/lander1" element={
                <Suspense fallback={<LoadingFallback />}>
                  <Lander1 />
                </Suspense>
              } />
              <Route path="/about" element={
                <Suspense fallback={<LoadingFallback />}>
                  <AboutUs />
                </Suspense>
              } />
              <Route path="/contact" element={
                <Suspense fallback={<LoadingFallback />}>
                  <Contact />
                </Suspense>
              } />
              {/* Pricing route removed
              <Route path="/pricing" element={
                <Suspense fallback={<LoadingFallback />}>
                  <PricingPage />
                </Suspense>
              } /> */}
              <Route path="/products" element={
                <Suspense fallback={<LoadingFallback />}>
                  <Products />
                </Suspense>
              } />
              <Route path="/services" element={
                <Suspense fallback={<LoadingFallback />}>
                  <Services />
                </Suspense>
              } />
              <Route path="/login" element={
                <Suspense fallback={<LoadingFallback />}>
                  <Auth />
                </Suspense>
              } />
              <Route path="/auth" element={
                <Suspense fallback={<LoadingFallback />}>
                  <Auth />
                </Suspense>
              } />
            </Route>
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        )}
      </div>
    </ThemeProvider>
  )
}

export default App